import api from '../helpers/api';
import { select, all, takeEvery, call, put } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import * as assetConstants from '../constants/assets';
import * as assetSelectors from '../selectors/assetSelectors';
import * as organizationSelectors from '../selectors/organizationSelectors';
import * as bucketSelectors from '../selectors/bucketSelectors';
import * as userSelectors from '../selectors/userSelectors';
import { getErrorMessage, uuid } from '../helpers/Util';
import { watchBatchActions } from './batchAssetsSaga';

import BackendPaths from '../helpers/BackendPaths';
import OtherApplicationsPaths from '../helpers/OtherApplicationsPaths';

import DEFAULT_ASSET from '../enums/DefaultAsset';
import { getAssetSearchSelectedTags, getAssetSearchTerm } from '../selectors/searchSelectors';
import { ASSET_TYPE } from '../enums/AssetType';

const LIMIT = 50;

function* fetchCharts(bucketId, offset, order, orderDirection, searchString, includedTags) {
    // set all query params required for the search
    const search = new URLSearchParams();
    search.append('ownersIds', bucketId);
    search.append('isDeleted', false);
    search.append('limit', LIMIT + 1);
    search.append('offset', offset);
    search.append('order', order);
    search.append('orderDirection', orderDirection);
    if (searchString && searchString.length) {
        search.append('q', searchString);
    }
    if (includedTags && includedTags.length) {
        includedTags.forEach(tag => {
            search.append('includedTags[]', tag);
        });
    }
    const path =
        BackendPaths.assets.chart.search.getPath() + '?' + search.toString();
    const response = yield call(api, path);
    const canLoadMore = response.projects.length > LIMIT;

    // map fields retrieved from server with fields that will be used in application
    const assets = response.projects.slice(0, LIMIT).map(chart => {
        let hasChangesAfterLastPublish = false;
        let embedURL;
        if (chart.firstPublishedAt) {
            hasChangesAfterLastPublish = chart.updatedAt > chart.lastPublishedAt;
            embedURL = OtherApplicationsPaths.embed.chart.getPath(chart);
        }
        return {
            ...chart,
            id: chart.id,
            title: chart.title,
            updatedAt: new Date(chart.updatedAt * 1000),
            hasChangesAfterLastPublish,
            visualizationType: chart.properties.visualizationType,
            previewURL: OtherApplicationsPaths.preview.chart.getPath(chart),
            embedURL,
        };
    });
    return { canLoadMore, assets };
}

function* fetchCurrencies(bucketId, offset, order, orderDirection, searchString, includedTags) {
    // set all query params required for the search
    const search = new URLSearchParams();
    search.append('ownersIds', bucketId);
    search.append('limit', LIMIT + 1);
    search.append('offset', offset);
    search.append('order', order);
    search.append('orderDirection', orderDirection);
    if (searchString && searchString.length) {
        search.append('q', searchString);
    }
    if (includedTags && includedTags.length) {
        includedTags.forEach(tag => {
            search.append('includedTags[]', tag);
        });
    }
    const path =
        BackendPaths.assets.currency.search.getPath() + '?' + search.toString();
    const response = yield call(api, path);
    const canLoadMore = response.projects.length > LIMIT;

    // map fields retrieved from server with fields that will be used in application
    const assets = response.projects.slice(0, LIMIT).map(currency => {
        let hasChangesAfterLastPublish = false;
        let embedURL;
        const updatedAt = currency.updatedAt;
        if (currency.firstPublishedAt) {
            hasChangesAfterLastPublish = updatedAt > currency.lastPublishedAt;
            embedURL = OtherApplicationsPaths.embed.currency.getPath(currency);
        }
        return {
            ...currency,
            id: currency.id,
            title: currency.title,
            updatedAt: new Date(currency.updatedAt * 1000),
            hasChangesAfterLastPublish,
            visualizationType: 'ceb',
            previewURL: OtherApplicationsPaths.preview.currency.getPath(
                currency,
            ),
            embedURL,
        };
    });
    return { canLoadMore, assets };
}

function* fetchSurveys(bucketId, offset, order, orderDirection, searchString, includedTags) {
    // set all query params required for the search
    const search = new URLSearchParams();
    search.append('ownerId', bucketId);
    search.append('limit', LIMIT + 1);
    search.append('offset', offset);
    search.append('order', order);
    search.append('orderDirection', orderDirection);
    if (searchString && searchString.length) {
        search.append('q', searchString);
    }
    if (includedTags && includedTags.length) {
        includedTags.forEach(tag => {
            search.append('includedTags[]', tag);
        });
    }
    const path =
        BackendPaths.assets.survey.project.getPath() + '?' + search.toString();
    const { projects } = yield call(api, path);
    const canLoadMore = projects.length > LIMIT;

    // map fields retrieved from server with fields that will be used in application
    const assets = projects.slice(0, LIMIT).map(survey => {
        let hasChangesAfterLastPublish = false;
        let embedURL, previewURL;
        const updatedAt = new Date(survey.updatedAt * 1000);
        if (survey.firstPublishedAt) {
            hasChangesAfterLastPublish =
                survey.updatedAt > survey.lastPublishedAt;
            embedURL = OtherApplicationsPaths.embed.survey.getPath(survey);
        }

        if (survey.properties.editType === 'wizard') {
            previewURL = OtherApplicationsPaths.preview.surveyWizard.getPath(
                survey,
            );
        } else {
            previewURL = OtherApplicationsPaths.preview.surveyEdit.getPath(
                survey,
            );
        }

        return {
            ...survey,
            id: survey.id,
            title: survey.title,
            updatedAt,
            hasChangesAfterLastPublish,
            visualizationType: 'survey',
            previewURL,
            embedURL,
        };
    });

    return { canLoadMore, assets };
}

function* fetchMapspiceMaps(bucketId, offset, order, orderDirection, searchString, includedTags) {
    const search = new URLSearchParams();
    search.append('ownerId', bucketId);
    search.append('limit', LIMIT + 1);
    search.append('offset', offset);
    search.append('order', order);
    search.append('orderDirection', orderDirection);
    if (searchString && searchString.length) {
        search.append('q', searchString);
    }
    if (includedTags && includedTags.length) {
        includedTags.forEach(tag => {
            search.append('includedTags[]', tag);
        });
    }
    const path =
        BackendPaths.assets.mapspice.project.getPath() +
        '?' +
        search.toString();
    const { projects } = yield call(api, path);
    const canLoadMore = projects.length > LIMIT;

    const assets = projects.slice(0, LIMIT).map(map => {
        let hasChangesAfterLastPublish = false;
        let embedURL, previewURL;
        const updatedAt = new Date(map.updatedAt * 1000);
        if (map.firstPublishedAt) {
            hasChangesAfterLastPublish = map.updatedAt > map.lastPublishedAt;
            embedURL = OtherApplicationsPaths.embed.mapspice.getPath(map);
        }

        if (map.properties.editType === 'wizard') {
            previewURL = OtherApplicationsPaths.preview.mapspiceWizard.getPath(
                map,
            );
        } else {
            previewURL = OtherApplicationsPaths.preview.mapspiceEdit.getPath(
                map,
            );
        }

        return {
            ...map,
            id: map.id,
            title: map.title,
            updatedAt,
            hasChangesAfterLastPublish,
            visualizationType: 'mapspice',
            previewURL,
            embedURL,
        };
    });

    return { canLoadMore, assets };
}

function* fetchLayouts(bucketId, offset, order, orderDirection, searchString, includedTags) {
    // set all query params required for the search
    const search = new URLSearchParams();
    search.append('ownerId', bucketId);
    search.append('limit', LIMIT + 1);
    search.append('offset', offset);
    search.append('order', order);
    search.append('orderDirection', orderDirection);
    if (searchString && searchString.length) {
        search.append('q', searchString);
    }
    if (includedTags && includedTags.length) {
        includedTags.forEach(tag => {
            search.append('includedTags[]', tag);
        });
    }
    const path =
        BackendPaths.assets.layout.project.getPath() + '?' + search.toString();
    const { projects } = yield call(api, path);
    const canLoadMore = projects.length > LIMIT;

    // layout fields retrieved from server with fields that will be used in application
    const assets = projects.slice(0, LIMIT).map(layout => {
        let hasChangesAfterLastPublish = false;
        let embedURL;
        const updatedAt = new Date(layout.updatedAt * 1000);
        if (layout.firstPublishedAt) {
            hasChangesAfterLastPublish =
                layout.updatedAt > layout.lastPublishedAt;
            embedURL = OtherApplicationsPaths.embed.layout.getPath(layout);
        }

        const previewURL = OtherApplicationsPaths.preview.layout.getPath({
            id: layout.id,
        });

        return {
            ...layout,
            id: layout.id,
            title: layout.title,
            updatedAt,
            hasChangesAfterLastPublish,
            visualizationType: 'layout',
            previewURL,
            embedURL,
        };
    });

    return { canLoadMore, assets };
}

function* fetchAllAssets(bucketId, offset, order, orderDirection, searchString, includedTags) {
    // TODO: Create endpoint for bulk fetch, but problem is that different asset type is on different microservice
    // Currently we fetch all types of assets and combine them
    const chartResult = yield fetchCharts(bucketId, offset, order, orderDirection, searchString, includedTags);
    const currencyResult = yield fetchCurrencies(bucketId, offset, order, orderDirection, searchString, includedTags);
    const surveyResult = yield fetchSurveys(bucketId, offset, order, orderDirection, searchString, includedTags);
    const mapspiceResult = yield fetchMapspiceMaps(bucketId, offset, order, orderDirection, searchString, includedTags);
    const layoutResult = yield fetchLayouts(bucketId, offset, order, orderDirection, searchString, includedTags);

    const combinedAssets = [
        ...chartResult.assets,
        ...currencyResult.assets,
        ...surveyResult.assets,
        ...mapspiceResult.assets,
        ...layoutResult.assets,
    ];

    let sortedAssets = [...combinedAssets];
    if (order === 'updatedAt') {
        sortedAssets = combinedAssets.sort((a, b) => {
            const dateA = new Date(a.updatedAt);
            const dateB = new Date(b.updatedAt);
            return orderDirection === 'desc' ? dateB - dateA : dateA - dateB;
        });
    } else if (order === 'title') {
        sortedAssets = combinedAssets.sort((a, b) => {
            const comparison = a.title.localeCompare(b.title);
            return orderDirection === 'desc' ? -comparison : comparison;
        });
    }

    return {
        canLoadMore: chartResult.canLoadMore || currencyResult.canLoadMore || surveyResult.canLoadMore || mapspiceResult.canLoadMore || layoutResult.canLoadMore,
        assets: sortedAssets.slice(0, LIMIT)
    };
}

function* fetchAssets(action) {
    const { bucketId, application } = action.payload;

    const includedTags = yield select(getAssetSearchSelectedTags);
    const searchTerm = yield select(getAssetSearchTerm);

    const applications = yield select(assetSelectors.getApplications);
    const offset = 0;
    const order = yield select(assetSelectors.getOrder);
    const orderDirection = yield select(assetSelectors.getOrderDirection);
    let result;
    try {
        switch (application) {
            case applications.all.id:
                result = yield fetchAllAssets(bucketId, offset, order, orderDirection, searchTerm, includedTags);
                break;
            case applications.chart.id:
                result = yield fetchCharts(bucketId, offset, order, orderDirection, searchTerm, includedTags);
                break;
            case applications.currency.id:
                result = yield fetchCurrencies(bucketId, offset, order, orderDirection, searchTerm, includedTags);
                break;
            case applications.survey.id:
                result = yield fetchSurveys(bucketId, offset, order, orderDirection, searchTerm, includedTags);
                break;
            case applications.mapspice.id:
                result = yield fetchMapspiceMaps(bucketId, offset, order, orderDirection, searchTerm, includedTags);
                break;
            case applications.layout.id:
                result = yield fetchLayouts(bucketId, offset, order, orderDirection, searchTerm, includedTags);
                break;
            default:
        }
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.FETCH_ASSETS_FAILURE,
            payload: {
                error: getErrorMessage(response),
                bucketId,
                application,
            },
        });
        return;
    }
    yield put({
        type: assetConstants.FETCH_ASSETS_SUCCESS,
        payload: {
            ...result,
            bucketId,
            application,
        },
    });
}

function* watchFetchAssets() {
    yield takeEvery(assetConstants.FETCH_ASSETS_REQUEST, fetchAssets);
}

function* loadMore() {
    const bucketId = yield select(assetSelectors.getBucketId);
    const application = yield select(assetSelectors.getApplication);
    const applications = yield select(assetSelectors.getApplications);
    const offset = yield select(assetSelectors.getOffset);
    const order = yield select(assetSelectors.getOrder);
    const orderDirection = yield select(assetSelectors.getOrderDirection);
    let result;
    try {
        // TODO why not use fetchAssets function? - duplicated code
        switch (application) {
            case applications.all.id:
                result = yield fetchAllAssets(bucketId, offset, order, orderDirection);
                break;
            case applications.chart.id:
                result = yield fetchCharts(bucketId, offset, order, orderDirection);
                break;
            case applications.currency.id:
                result = yield fetchCurrencies(bucketId, offset, order, orderDirection);
                break;
            case applications.survey.id:
                result = yield fetchSurveys(bucketId, offset, order, orderDirection);
                break;
            case applications.mapspice.id:
                result = yield fetchMapspiceMaps(bucketId, offset, order, orderDirection);
                break;
            case applications.layout.id:
                result = yield fetchLayouts(bucketId, offset, order, orderDirection);
                break;
            default:
        }
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.LOAD_MORE_ASSETS_FAILURE,
            payload: {
                error: getErrorMessage(response),
                bucketId,
                application,
            },
        });
        return;
    }
    yield put({
        type: assetConstants.LOAD_MORE_ASSETS_SUCCESS,
        payload: {
            ...result,
            bucketId,
            application,
        },
    });
}

function* watchLoadMore() {
    yield takeEvery(assetConstants.LOAD_MORE_ASSETS_REQUEST, loadMore);
}

function* createNewSurveyAsset(bucketId, assetSetup) {
    const path = BackendPaths.assets.survey.project.getPath();
    let defaultSurvey = DEFAULT_ASSET.SURVEY;
    if (assetSetup.assetEditType === 'wizard') {
        defaultSurvey = DEFAULT_ASSET.SURVEY_WIZARD;
        const organizationId = yield select(
            organizationSelectors.getCurrentOrganizationId,
        );
        if (
            organizationId.toString() ===
            process.env.REACT_APP_PEARSON_ORGANIZATION_ID.toString()
        ) {
            defaultSurvey.context = 'PEARSON';
        }
    }

    return yield call(api, path, 'POST', {
        ownerId: bucketId,
        definition: {
            ...defaultSurvey,
            title: assetSetup.title,
        },
        editType: assetSetup.assetEditType,
    });
}

function* createNewMapspiceAsset(bucketId, assetSetup) {
    const path = BackendPaths.assets.mapspice.project.getPath();
    let definition = DEFAULT_ASSET.MAPSPICE_MAP;
    if (assetSetup.assetEditType === 'wizard') {
        definition = DEFAULT_ASSET.MAPSPICE_WIZARD_MAP;
    }

    definition.projectName = assetSetup.title;

    return yield call(api, path, 'POST', {
        ownerId: bucketId,
        definition: {
            ...definition,
        },
        editType: assetSetup.assetEditType,
    });
}

function* createNewChartAsset(bucketId, assetSetup) {
    const path = BackendPaths.assets.chart.create.getPath();
    let definition = DEFAULT_ASSET.CHART;
    definition.project.title = assetSetup.title;
    definition.project.ownerId = bucketId;

    return yield call(api, path, 'POST', { definition });
}

function* createNewCurrencyAsset(bucketId, bucketName, assetSetup) {
    const path = BackendPaths.assets.currency.create.getPath();
    let payload = DEFAULT_ASSET.CURRENCY;
    payload.book = bucketName;
    payload.definition.ownerId = bucketId;
    payload.definition.projectName = assetSetup.title;

    return yield call(api, path, 'POST', { ...payload });
}

function* createNewLayoutAsset(bucketId, assetSetup) {
    const path = BackendPaths.assets.layout.project.getPath();
    const definition = { title: assetSetup.title };

    return yield call(api, path, 'POST', { ownerId: bucketId, definition });
}

function* updateSurveyAsset(surveyId, updated) {
    const path = BackendPaths.assets.survey.update.getPath({ surveyId });
    let payload;
    if (updated.title) {
        payload = {
            title: updated.title,
        };
    } else if (updated.ownerId) {
        payload = {
            ownerId: updated.ownerId,
        };
    } else if (updated.tags) {
        payload = {
            tags: updated.tags
        }
    }

    return yield call(api, path, 'POST', payload);
}

function* updateMapspiceAsset(mapId, updated) {
    const path = BackendPaths.assets.mapspice.update.getPath({ mapId });
    let payload;
    if (updated.title) {
        payload = {
            projectName: updated.title,
        };
    } else if (updated.ownerId) {
        payload = {
            ownerId: updated.ownerId,
        };
    } else if (updated.tags) {
        payload = {
            tags: updated.tags
        }
    }
    return yield call(api, path, 'POST', payload);
}

function* updateChartAsset(chartId, updated) {
    const path = BackendPaths.assets.chart.update.getPath({ chartId });

    const payload = {
        messageId: uuid(),
        action: 'projectEdit',
        up: {
            propertyUpdate: updated,
        },
    };
    return yield call(api, path, 'POST', payload);
}

function* updateCurrencyAsset(currencyId, updated) {
    const path = BackendPaths.assets.currency.update.getPath({ currencyId });
    const payload = {
        definition: {
            projectName: updated.title,
            tags: updated.tags,
        },
    };

    return yield call(api, path, 'POST', payload);
}

function* updateLayoutAsset(layoutId, updated) {
    const path = BackendPaths.assets.layout.update.getPath({ layoutId });
    let payload;
    if (updated.title) {
        payload = {
            title: updated.title,
            tags: updated.tags,
        };
    } else if (updated.ownerId) {
        payload = {
            ownerId: updated.ownerId,
        };
    } else if (updated.tags) {
        payload = {
            tags: updated.tags
        }
    }
    return yield call(api, path, 'POST', payload);
}

function* cloneChartAsset(chart, ownerId) {
    const path = BackendPaths.assets.chart.bulkClone.getPath();

    const payload = {
        projects: [
            {
                id: chart.id,
                title: `${chart.title} - Copy`,
            },
        ],
        ownerId,
    };

    return yield call(api, path, 'POST', payload);
}

function* cloneCurrencyAsset(currency, ownerId) {
    const path = BackendPaths.assets.currency.bulkClone.getPath();

    const payload = {
        projects: [
            {
                id: currency.id,
                projectName: `${currency.title} - Copy`,
            },
        ],
        ownerId,
    };

    return yield call(api, path, 'POST', payload);
}

function* cloneSurveyAsset(survey, ownerId) {
    const path = BackendPaths.assets.survey.clone.getPath({
        surveyId: survey.id,
    });

    const payload = {
        newTitle: `${survey.title} - Copy`,
        ownerId,
    };

    return yield call(api, path, 'POST', payload);
}

function* cloneMapspiceAsset(map, ownerId) {
    const path = BackendPaths.assets.mapspice.clone.getPath({ mapId: map.id });

    const payload = {
        newTitle: `${map.title} - Copy`,
        ownerId,
    };

    return yield call(api, path, 'POST', payload);
}

function* cloneLayoutAsset(layout, ownerId) {
    const path = BackendPaths.assets.layout.clone.getPath({
        layoutId: layout.id,
    });

    const payload = {
        newTitle: `${layout.title} - Copy`,
        ownerId,
    };

    return yield call(api, path, 'POST', payload);
}

function* transferChartAssetOwnership(chartId, ownerId) {
    const path = BackendPaths.assets.chart.bulkMove.getPath();

    const payload = {
        ids: [chartId],
        ownerId,
    };

    return yield call(api, path, 'POST', payload);
}

function* transferCurrencyAssetOwnership(currencyId, ownerId) {
    const path = BackendPaths.assets.currency.bulkMove.getPath();

    const payload = {
        ids: [currencyId],
        ownerId,
    };

    return yield call(api, path, 'POST', payload);
}

function* createAsset(action) {
    const assetSetup = action.payload;

    const applications = yield select(assetSelectors.getApplications);
    const bucketId = yield select(assetSelectors.getBucketId);
    const bucketName = yield select(bucketSelectors.getCurrentBucketName);
    const application = yield select(assetSelectors.getApplication);
    let result;
    let previewURL;
    let id;
    try {
        if (assetSetup.isAlreadyCreated) {
            result = assetSetup;
        } else {
            switch (application) {
                case applications.survey.id:
                    result = yield createNewSurveyAsset(bucketId, assetSetup);
                    break;
                case applications.mapspice.id:
                    result = yield createNewMapspiceAsset(bucketId, assetSetup);
                    break;
                case applications.chart.id:
                    result = yield createNewChartAsset(bucketId, assetSetup);
                    break;
                case applications.currency.id:
                    result = yield createNewCurrencyAsset(
                        bucketId,
                        bucketName,
                        assetSetup,
                    );
                    break;
                case applications.layout.id:
                    result = yield createNewLayoutAsset(bucketId, assetSetup);
                    break;
                default:
            }
        }
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.NEW_ASSET_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }

    if (result) {
        yield put({
            type: assetConstants.NEW_ASSET_SUCCESS,
            payload: {
                result,
                bucketId,
                application,
            },
        });

        if (result.metadata) {
            id = result.metadata.id;
        } else if (result.project) {
            id = result.project.id;
        }

        switch (application) {
            case applications.survey.id:
                if (result.metadata.editType === 'wizard') {
                    previewURL = OtherApplicationsPaths.preview.surveyWizard.getPath(
                        {
                            id,
                        },
                    );
                } else {
                    previewURL = OtherApplicationsPaths.preview.surveyEdit.getPath(
                        {
                            id,
                        },
                    );
                }
                break;
            case applications.mapspice.id:
                if (result.metadata.editType === 'wizard') {
                    previewURL = OtherApplicationsPaths.preview.mapspiceWizard.getPath(
                        {
                            id,
                        },
                    );
                } else {
                    previewURL = OtherApplicationsPaths.preview.mapspiceEdit.getPath(
                        {
                            id,
                        },
                    );
                }
                break;
            case applications.chart.id:
                previewURL = OtherApplicationsPaths.preview.chartEdit.getPath({
                    id,
                });
                break;
            case applications.currency.id:
                previewURL = OtherApplicationsPaths.preview.currency.getPath({
                    id,
                });
                break;
            case applications.layout.id:
                previewURL = OtherApplicationsPaths.preview.layout.getPath({
                    id,
                });
                break;
            default:
        }

        window.open(previewURL);

        yield delay(500); // Wait for the asset to be created before fetching
        yield put({
            type: assetConstants.FETCH_ASSETS_REQUEST,
            payload: {
                bucketId,
                application,
            },
        });
    }
}

function* watchCreateAsset() {
    yield takeEvery(assetConstants.NEW_ASSET_REQUEST, createAsset);
}

export function* moveAsset(action) {
    const { asset, toBucket, isCopy, isDuplicate } = action.payload;

    let result;

    try {
        switch (asset.context) {
            case ASSET_TYPE.SURVEY:
                if (isCopy || isDuplicate) {
                    result = yield cloneSurveyAsset(asset, toBucket.id);
                } else {
                    result = yield updateSurveyAsset(asset.id, {
                        ownerId: toBucket.id,
                    });
                }
                break;
            case ASSET_TYPE.MAPSPICE:
                if (isCopy || isDuplicate) {
                    result = yield cloneMapspiceAsset(asset, toBucket.id);
                } else {
                    result = yield updateMapspiceAsset(asset.id, {
                        ownerId: toBucket.id,
                    });
                }
                break;
            case ASSET_TYPE.CHART:
            case ASSET_TYPE.TABLE:
                if (isCopy || isDuplicate) {
                    result = yield cloneChartAsset(asset, toBucket.id);
                } else {
                    result = yield transferChartAssetOwnership(
                        asset.id,
                        toBucket.id,
                    );
                }
                break;
            case ASSET_TYPE.CEB:
            case ASSET_TYPE.CURRENCY:
                if (isCopy || isDuplicate) {
                    result = yield cloneCurrencyAsset(asset, toBucket.id);
                } else {
                    result = yield transferCurrencyAssetOwnership(
                        asset.id,
                        toBucket.id,
                    );
                }
                break;
            case ASSET_TYPE.LAYOUT:
                if (isCopy || isDuplicate) {
                    result = yield cloneLayoutAsset(asset, toBucket.id);
                } else {
                    result = yield updateLayoutAsset(asset.id, {
                        ownerId: toBucket.id,
                    });
                }
                break;
            default:
        }
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.MOVE_ASSET_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
                isCopy,
                isDuplicate,
            },
        });
    }

    if (result) {
        yield put({
            type: assetConstants.MOVE_ASSET_SUCCESS,
            payload: {
                asset,
                isCopy,
                isDuplicate,
                result,
            },
        });
    }
}

function* watchMoveAsset() {
    yield takeEvery(assetConstants.MOVE_ASSET_REQUEST, moveAsset);
}

export function* updateAsset(action) {
    const { asset, updatedAsset } = action.payload;

    let result;

    try {
        switch (asset.context) {
            case ASSET_TYPE.SURVEY:
                result = yield updateSurveyAsset(asset.id, updatedAsset);
                break;
            case ASSET_TYPE.MAPSPICE:
                result = yield updateMapspiceAsset(asset.id, updatedAsset);
                break;
            case ASSET_TYPE.TABLE:
            case ASSET_TYPE.CHART:
                result = yield updateChartAsset(asset.id, updatedAsset);
                break;
            case ASSET_TYPE.CEB:
            case ASSET_TYPE.CURRENCY:
                result = yield updateCurrencyAsset(asset.id, updatedAsset);
                break;
            case ASSET_TYPE.LAYOUT:
                result = yield updateLayoutAsset(asset.id, updatedAsset);
                break;
            default:
        }
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.UPDATE_ASSET_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }

    if (result) {
        result = {
            ...asset,
            ...updatedAsset,
        };

        yield put({
            type: assetConstants.UPDATE_ASSET_SUCCESS,
            payload: {
                asset: result,
            },
        });
    }
}

function* watchUpdateAsset() {
    yield takeEvery(assetConstants.UPDATE_ASSET_REQUEST, updateAsset);
}

export function* deleteAsset(action) {
    const { asset } = action.payload;

    let path;
    let result;

    try {
        switch (asset.context) {
            case ASSET_TYPE.SURVEY:
                path = BackendPaths.assets.survey.delete.getPath({
                    surveyId: asset.id,
                });
                result = yield call(api, path, 'DELETE');
                break;
            case ASSET_TYPE.MAPSPICE:
                path = BackendPaths.assets.mapspice.delete.getPath({
                    mapId: asset.id,
                });
                result = yield call(api, path, 'DELETE');
                break;
            case ASSET_TYPE.CHART:
            case ASSET_TYPE.TABLE:
                path = BackendPaths.assets.chart.softDelete.getPath({
                    chartId: asset.id,
                });
                result = yield call(api, path, 'POST');
                break;
            case ASSET_TYPE.CEB:
            case ASSET_TYPE.CURRENCY:
                path = BackendPaths.assets.currency.bulkDelete.getPath();
                result = yield call(api, path, 'POST', { ids: [asset.id] });
                break;
            case ASSET_TYPE.LAYOUT:
                path = BackendPaths.assets.layout.delete.getPath({
                    layoutId: asset.id,
                });
                result = yield call(api, path, 'DELETE');
                break;
            default:
        }
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.DELETE_ASSET_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }

    if (result) {
        yield put({
            type: assetConstants.DELETE_ASSET_SUCCESS,
            payload: {
                asset,
            },
        });
    }
}

function* watchDeleteAsset() {
    yield takeEvery(assetConstants.DELETE_ASSET_REQUEST, deleteAsset);
}

export function* publishAsset(action) {
    const { asset } = action.payload;

    let path;
    let result;

    try {
        switch (asset.context) {
            case ASSET_TYPE.TABLE:
            case ASSET_TYPE.CHART:
                path = BackendPaths.assets.chart.publish.getPath({ chartId: asset.id });
                result = yield call(api, path, 'POST');
                break;
            case ASSET_TYPE.MAPSPICE:
                path = BackendPaths.assets.mapspice.publish.getPath({
                    mapId: asset.id,
                });
                result = yield call(api, path, 'POST');
                break;
            case ASSET_TYPE.SURVEY:
                path = BackendPaths.assets.survey.publish.getPath({
                    surveyId: asset.id,
                });
                result = yield call(api, path, 'POST');
                break;
            case ASSET_TYPE.CEB:
            case ASSET_TYPE.CURRENCY:
                path = BackendPaths.assets.currency.publish.getPath({
                    currencyId: asset.id,
                });
                break;
            case ASSET_TYPE.LAYOUT:
                path = BackendPaths.assets.layout.publish.getPath({
                    layoutId: asset.id,
                });
                break;
            default:
        }

        if (path) {
            result = yield call(api, path, 'POST');
        }

    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.PUBLISH_ASSET_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }

    if (result) {
        yield put({
            type: assetConstants.PUBLISH_ASSET_SUCCESS,
            payload: {
                asset
            },
        });
    }
}

function* watchPublishAsset() {
    yield takeEvery(assetConstants.PUBLISH_ASSET_REQUEST, publishAsset);
}


function* exportProjectImages(action) {
    const { exportTables, exportCharts, format: payloadFormat, selectedAssetsIds, withBackground } = action.payload;

    try {
        const projectId = yield select(assetSelectors.getBucketId);
        const path =
            BackendPaths.assets.chart.publishProjectTablesToImage.getPath({
                projectId,
            });
        const format = payloadFormat || 'png';
        const payload = {
            exportOptions: {
                type: format,
            },
            includeTables: exportTables,
            includeCharts: exportCharts,
            widgetsIds: selectedAssetsIds
        };

        if (format === 'png') {
            payload.exportOptions.withBackground = withBackground
        }

        const response = yield call(api, path, 'POST', payload);

        yield put({
            type: assetConstants.EXPORT_PROJECT_IMAGES_SUCCESS,
            payload: {
                jobId: response.jobId,
                count: response.count,
            },
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: assetConstants.EXPORT_PROJECT_IMAGES_FAILURE,
            error: true,
            payload: {
                error: getErrorMessage(response),
            },
        });
    }
}

function* watchExportProjectImages() {
    yield takeEvery(
        assetConstants.EXPORT_PROJECT_IMAGES_REQUEST,
        exportProjectImages
    );
}

export default function* () {
    yield all([
        watchFetchAssets(),
        watchLoadMore(),
        watchCreateAsset(),
        watchMoveAsset(),
        watchUpdateAsset(),
        watchDeleteAsset(),
        watchPublishAsset(),
        watchBatchActions(),
        watchExportProjectImages(), // NEW
    ]);
}



// WEBPACK FOOTER //
// ./src/sagas/assetsSaga.js